import { useEffect, useRef, useState, type RefObject } from 'react'

export type RevealAttribute = 'reveal' | 'draw' | 'seats'

export function prefersReducedMotion() {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false
}

export function hasFinePointer() {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: fine)').matches
    : false
}

export function startsBelowTheFold(element: Element) {
  return element.getBoundingClientRect().top > window.innerHeight * 0.9
}

export function useReveal<T extends HTMLElement | SVGElement>(
  attribute: RevealAttribute = 'reveal',
) {
  const ref = useRef<T>(null)

  useEffect(() => {
    const element = ref.current
    if (!element || typeof window.IntersectionObserver !== 'function') return
    if (prefersReducedMotion() || !startsBelowTheFold(element)) return
    element.dataset[attribute] = 'pending'
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        element.dataset[attribute] = 'shown'
        observer.disconnect()
      },
      { rootMargin: '0px 0px -8% 0px' },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [attribute])

  return ref
}

export function useFirstView<T extends Element>(ref: RefObject<T | null>) {
  const [seen, setSeen] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element || typeof window.IntersectionObserver !== 'function') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        setSeen(true)
        observer.disconnect()
      },
      { rootMargin: '0px 0px -10% 0px' },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])

  return seen
}

export function useSpotlight<T extends HTMLElement>() {
  const ref = useRef<T>(null)

  useEffect(() => {
    const root = ref.current
    if (!root || !hasFinePointer() || prefersReducedMotion()) return
    let frame = 0
    let pointer = { x: 0, y: 0 }
    const paint = () => {
      frame = 0
      for (const card of root.querySelectorAll<HTMLElement>('.spotlight-card')) {
        const rect = card.getBoundingClientRect()
        card.style.setProperty('--spot-x', `${pointer.x - rect.left}px`)
        card.style.setProperty('--spot-y', `${pointer.y - rect.top}px`)
      }
      const rect = root.getBoundingClientRect()
      root.style.setProperty('--spot-x', `${pointer.x - rect.left}px`)
      root.style.setProperty('--spot-y', `${pointer.y - rect.top}px`)
    }
    const onMove = (event: PointerEvent) => {
      pointer = { x: event.clientX, y: event.clientY }
      if (!frame) frame = window.requestAnimationFrame(paint)
    }
    root.addEventListener('pointermove', onMove, { passive: true })
    return () => {
      root.removeEventListener('pointermove', onMove)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [])

  return ref
}

export function useTilt<T extends HTMLElement>(maxDegrees = 6) {
  const ref = useRef<T>(null)

  useEffect(() => {
    const element = ref.current
    if (!element || !hasFinePointer() || prefersReducedMotion()) return
    let frame = 0
    let target = { x: 0, y: 0 }
    const paint = () => {
      frame = 0
      element.style.setProperty('--tilt-x', `${target.x}deg`)
      element.style.setProperty('--tilt-y', `${target.y}deg`)
    }
    const onMove = (event: PointerEvent) => {
      const rect = element.getBoundingClientRect()
      const px = (event.clientX - rect.left) / rect.width - 0.5
      const py = (event.clientY - rect.top) / rect.height - 0.5
      target = { x: -py * maxDegrees, y: px * maxDegrees }
      if (!frame) frame = window.requestAnimationFrame(paint)
    }
    const onLeave = () => {
      target = { x: 0, y: 0 }
      if (!frame) frame = window.requestAnimationFrame(paint)
    }
    element.addEventListener('pointermove', onMove, { passive: true })
    element.addEventListener('pointerleave', onLeave)
    return () => {
      element.removeEventListener('pointermove', onMove)
      element.removeEventListener('pointerleave', onLeave)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [maxDegrees])

  return ref
}
